import axios, { AxiosError, type AxiosInstance } from "axios";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

// =============================================================================
// Cache de session Supabase — evite la contention du lock navigator.locks
// =============================================================================
// `supabase.auth.getSession()` prend un lock global (Web Locks API depuis
// gotrue-js v2). Quand plusieurs composants React montent en parallele
// (CockpitDashboard + NotificationsDrawer + ProfileHeader + ...), ils
// declenchent chacun leur getSession() qui se met en file d'attente sur
// le meme lock. En prod observe : sequence 8ms -> 2006ms -> 5004ms (le
// lock timeout deDefault de gotrue-js), avec le warning explicite :
//   "Lock 'lock:sb-...-auth-token' was not released within 5000ms"
//
// Strategie : un seul getSession() au boot (resolu lazy + memoise), puis
// mise a jour reactive via onAuthStateChange (SIGNED_IN, TOKEN_REFRESHED,
// SIGNED_OUT, USER_UPDATED). L'intercepteur axios lit ensuite directement
// `_sessionCache.session` -> instant, pas de lock.
//
// Resilience : si getSession() echoue (Safari ITP a purge le storage),
// on garde session=null, l'intercepteur n'ajoute pas d'Authorization,
// le backend renvoie 401, classifyApiError mappe sur "Authentification
// requise" -> ProtectedRoute force la reconnexion (UX correcte).
const _sessionCache: { session: Session | null; ready: Promise<void> | null } =
  {
    session: null,
    ready: null,
  };

function _initSessionCache(): Promise<void> {
  if (_sessionCache.ready) return _sessionCache.ready;
  _sessionCache.ready = supabase.auth
    .getSession()
    .then(({ data }) => {
      _sessionCache.session = data.session;
    })
    .catch(err => {
      // eslint-disable-next-line no-console
      console.warn(
        "[api] supabase.auth.getSession() initiale a echoue ; sessions Authorization ignorees jusqu'a un nouveau SIGNED_IN. Storage Safari purge ?",
        err
      );
      _sessionCache.session = null;
    });
  return _sessionCache.ready;
}

// Maintien reactif du cache : tout changement (login, refresh auto du
// access_token toutes les ~55min, logout) propage immediatement sans
// re-prendre le lock.
supabase.auth.onAuthStateChange((_event, session) => {
  _sessionCache.session = session;
});

// Eager load au module init : la promesse demarre des l'import,
// le premier appel intercepteur attend ce single getSession().
_initSessionCache();

// =============================================================================
// Resolution de la baseURL API
// =============================================================================
// Strategie : eviter le cross-origin quand on est servi depuis le domaine
// de production (gsfarbitral.ai). Le vercel.json proxifie `/api/*` vers
// le backend Railway -> du point de vue du navigateur, c'est du same-
// origin, donc PAS de preflight CORS et PAS de blocage par les
// politiques privacy des navigateurs (Safari ITP, iCloud Private Relay,
// extensions anti-tracking, ...).
//
// Sur les previews Vercel (*.vercel.app) et en dev local, on garde
// l'URL absolue Railway -- les previews appellent directement le core
// via CORS (deja whitelist cote backend).
//
// Cas empirique resolu par cette strategie : ERR_NETWORK / "Network
// Error" sur Safari iPad quand on faisait des XHR cross-origin avec
// entete Authorization vers railway.app (preflight bloque cote
// navigateur sans qu'aucun log serveur n'apparaisse).
const PRODUCTION_HOSTNAME_REGEX = /(^|\.)gsfarbitral\.ai$/i;

export function resolveBaseUrl(): string {
  if (
    typeof window !== "undefined" &&
    PRODUCTION_HOSTNAME_REGEX.test(window.location.hostname)
  ) {
    // baseURL vide -> axios fait des requetes relatives sur le meme
    // domaine -> Vercel rewrite vers Railway -> same-origin pour le
    // navigateur.
    return "";
  }
  return (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: resolveBaseUrl(),
  timeout: 15000,
});

// Retourne true si le token expires_at (en secondes Unix) est déjà passé
// ou expirera dans moins de 30 s — délai couvrant la latence réseau.
function _isTokenExpired(session: Session): boolean {
  if (!session.expires_at) return false;
  return session.expires_at * 1000 < Date.now() + 30_000;
}

apiClient.interceptors.request.use(async config => {
  // Cold start : attend le getSession() initial unique (cf. _initSessionCache).
  // Une fois resolu, les requetes suivantes lisent _sessionCache.session en
  // memoire -> zero overhead, zero contention de lock navigator.locks.
  if (_sessionCache.ready && _sessionCache.session === null) {
    try {
      await _sessionCache.ready;
    } catch {
      // Init en erreur -> _sessionCache.session reste null. On continue
      // sans Authorization, le backend renverra 401, classifyApiError
      // mappe en "Authentification requise" (UX correcte).
    }
  }

  // Rafraichissement proactif si le token est expire ou expirera dans 30 s.
  // Cas typique : onglet en veille -> timer auto-refresh Supabase ne s'est
  // pas declenche -> token perime en cache -> toutes les requetes → 401.
  // Supabase deduplique les appels concurrents a refreshSession en interne.
  if (_sessionCache.session && _isTokenExpired(_sessionCache.session)) {
    try {
      const { data } = await supabase.auth.refreshSession();
      _sessionCache.session = data.session;
    } catch {
      // Refresh echoue (refresh token expire, reseau indisponible...).
      // On continue avec le token perime ; le backend renverra 401 et
      // ProtectedRoute redirigera vers la page de connexion.
    }
  }

  const token = _sessionCache.session?.access_token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Reponse : detecte les 401 globalement et logue. Eviter une boucle de
// redirect ici (le ProtectedRoute gere deja la deconnexion). On se
// contente de signaler clairement le 401 dans la console pour faciliter
// le diagnostic depuis l'inspecteur web Safari.
apiClient.interceptors.response.use(
  resp => resp,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      // eslint-disable-next-line no-console
      console.warn(
        "[api] 401 sur",
        err.config?.url,
        "— session expiree ou absente. L'utilisateur doit se reconnecter."
      );
    }
    return Promise.reject(err);
  }
);

// --- Guard de reponse "liste" ----------------------------------------
//
// Cause empirique du crash NotificationsDrawer (TypeError: n?.map is
// not a function) : quand VITE_API_BASE_URL n'est pas configure sur
// Vercel, axios resout les chemins /api/v1/* sur le domaine de la
// vitrine elle-meme. Le SPA fallback renvoie alors index.html (HTML
// brut) avec status 200, axios met cette chaine dans `data`, et le
// composant essaie .map() sur une string -> crash.
//
// `ensureArray` blinde tous les endpoints qui DOIVENT retourner un
// tableau : si la reponse n'en est pas un, on renvoie [] et on logge
// un warning explicite avec la cause probable. Plus aucun composant
// ne re-recoit autre chose qu'un Array depuis ces helpers.
function ensureArray<T>(value: unknown, endpoint: string): T[] {
  if (Array.isArray(value)) return value as T[];
  // eslint-disable-next-line no-console
  console.warn(
    `[api] ${endpoint}: la reponse n'est pas un tableau. Cause probable : ` +
      `VITE_API_BASE_URL non configure (le SPA renvoie son index.html ` +
      `sur /api/v1/* au lieu d'appeler le core FastAPI).`,
    { receivedType: typeof value, preview: String(value).slice(0, 80) }
  );
  return [];
}

// --- Types ------------------------------------------------------------

export interface MeetingTokenResponse {
  domain: string;
  room_name: string;
  token: string;
  ttl_seconds: number;
  is_moderator: boolean;
  simulated: boolean;
}

export interface ConsentResponse {
  evidence_bundle_id: string;
  consent_type: string;
  canonical_payload_hash: string;
  is_high_strength_proof: boolean;
  merkle_root_snapshot: string | null;
  message: string;
}

export interface ConsentStatusResponse {
  consent_type: string;
  consent_text_version: string;
  accepted: boolean;
  evidence_bundle_id: string | null;
  consented_at: string | null;
}

export interface CaseSummary {
  id: number;
  numero_gsf: string;
  objet_litige: string;
  statut: string;
  date_creation: string | null;
  deadline_instruction: string | null;
  anchored_status: string;
}

export interface DocumentInCaseSummary {
  id: number;
  evidence_bundle_id: string | null;
  original_filename: string;
  secure_mime_type: string | null;
  file_size_bytes: number | null;
  file_integrity_hash: string;
  role_at_upload: string | null;
  uploader_id: string | null;
  date_creation: string | null;
  has_tsa_token: boolean;
  has_merkle_proof: boolean;
}

export interface CaseMemberSummary {
  user_id: string;
  member_role: string;
  date_creation: string | null;
}

export interface CaseDetail {
  id: number;
  numero_gsf: string;
  objet_litige: string;
  statut: string;
  phase_procedurale: string | null;
  date_creation: string | null;
  deadline_instruction: string | null;
  anchored_status: string;
  blockchain_tx_hash: string | null;
  anchored_merkle_root: string | null;
  anchored_at: string | null;
  explorer_url: string | null;
  documents: DocumentInCaseSummary[];
  members: CaseMemberSummary[];
}

export interface DocumentUploadResponse {
  evidence_bundle_id: string;
  document_id: number;
  original_filename: string;
  secure_mime_type: string | null;
  file_size_bytes: number;
  file_integrity_hash: string;
  merkle_root_snapshot: string | null;
  zetes_anchor_id: string | null;
  message: string;
}

export interface AnchorStatusResponse {
  dossier_id: number;
  anchored_status: string;
  tx_hash: string | null;
  anchored_merkle_root: string | null;
  anchored_at: string | null;
  block: number | null;
  explorer_url?: string | null;
}

export interface AnchorTriggerResponse {
  message: string;
  dossier_id: number;
  tx_hash: string;
  anchored_status: string;
  anchored_merkle_root: string;
  explorer_url: string;
}

export type DossierStatut = "OUVERT" | "INSTRUCTION" | "CLOTURE" | "TERMINE";

export interface LegalTransitionsResponse {
  current_state: DossierStatut;
  legal_transitions: DossierStatut[];
}

export interface ValidateStepResponse {
  dossier_id: number;
  new_state: string;
  deadline_instruction: string | null;
}

export interface NotificationOut {
  id: number;
  dossier_id: number;
  title: string;
  message: string;
  reference_dossier: string;
  channel: string;
  status: string;
  created_at: string;
  read_at: string | null;
}

export interface ProfileOut {
  /** UUID Postgres -- depuis le rename backend du 2026-05-16,
   *  remplace l'ancien champ `user_id` (qui causait un 500 lors de
   *  la serialisation Pydantic). */
  id: string;
  role: string | null;
  full_name: string;
  specialty: string | null;
  years_of_experience: number | null;
  cv_url: string | null;
  languages: string[] | null;
  // --- Profil avocat (CCJA) ---
  titre: string | null;
  barreaux: string | null;
  role_arbitrage: RoleArbitrage | null;
  institutions_arbitrage: string[] | null;
  secteurs: string[] | null;
  pays_ohada: string[] | null;
  accepte_nominations: boolean | null;
  is_verified: boolean;
  verified_by: string | null;
  verified_at: string | null;
  admin_notes: string | null;
  ai_onboarding_summary: string | null;
  created_at: string;
}

export type RoleArbitrage = "ARBITRE" | "CONSEIL" | "LES_DEUX";

export interface ProfileUpdate {
  full_name?: string;
  specialty?: string;
  years_of_experience?: number;
  cv_url?: string;
  languages?: string[];
  // --- Profil avocat (CCJA) ---
  titre?: string;
  barreaux?: string;
  role_arbitrage?: RoleArbitrage;
  institutions_arbitrage?: string[];
  secteurs?: string[];
  pays_ohada?: string[];
  accepte_nominations?: boolean;
}

export interface RAGSource {
  id: string;
  text: string;
  source: string;
}

export interface RechercheResponse {
  answer: string;
  sources_used: RAGSource[];
  model: string;
  simulated: boolean;
}

// --- Assistant juridique v2 (Lots 2-3-6 core) -------------------------

export type ResponseMode =
  | "quick"
  | "legal_note"
  | "counter_argument"
  | "audit";
export type ConfidenceLabel = "low" | "medium" | "high";
export type SupportLevel =
  | "explicit"
  | "derivative"
  | "ambiguous"
  | "unsupported";
export type LegalCategory =
  | "droit_positif"
  | "jurisprudence"
  | "doctrine"
  | "note_interne";
export type RefusalType = "prudent" | "refus" | "escalade";

export interface SyllogismV2 {
  major: string;
  minor: string;
  conclusion: string;
}

export interface ClaimV2 {
  claim_id: string;
  text: string;
  legal_category: LegalCategory;
  source_ids: string[];
  quote: string | null;
  support_level: SupportLevel;
  // PR C — liens internes derives par le backend (`computed_field` Pydantic).
  // Format : ["/corpus/<source_id>", ...]. Ordre stable identique a source_ids.
  // Optionnel pour retro-compat avec un backend pre-PR C.
  corpus_links?: string[];
}

/**
 * Document source du corpus (PR C). Reponse de GET /api/v1/corpus/{source_id}.
 * Aligne sur le schema backend `CorpusDocument` (Pydantic).
 */
export interface CorpusDocument {
  id: string;
  content: string;
  source: string;
  source_type: string | null;
  jurisdiction: string | null;
  legal_force: string | null;
  metadata: Record<string, unknown>;
}

export interface ConfidenceV2 {
  score: number;
  label: ConfidenceLabel;
  reasons: string[];
}

export interface RefusalV2 {
  type: RefusalType;
  message: string;
}

export interface RAGSourceV2 {
  id: string;
  text: string;
  source: string;
  score: number | null;
  type: string | null;
  jurisdiction: string | null;
  legal_force: string | null;
}

export interface ClassificationV2 {
  domain: string;
  type: string;
  criticality: number;
  needs_text: boolean;
  needs_jurisprudence: boolean;
  needs_human_review: boolean;
}

export type CoverageLevel = "complete" | "partial" | "weak";

export interface CoverageReportV2 {
  total_claims: number;
  supported: number;
  weak: number;
  unsupported: number;
  coverage_ratio: number;
  level: CoverageLevel;
  unsupported_claim_ids: string[];
  message: string;
}

export interface RechercheResponseV2 {
  answer: string;
  syllogism: SyllogismV2;
  claims: ClaimV2[];
  confidence: ConfidenceV2;
  classification: ClassificationV2;
  sources_used: RAGSourceV2[];
  refusal: RefusalV2 | null;
  needs_human_review: boolean;
  model: string;
  simulated: boolean;
  audit_id: number | null;
  mode: ResponseMode;
  // Optionnel : absent si le backend n'expose pas encore le rapport (rétro-compat).
  coverage?: CoverageReportV2 | null;
}

// --- Decisions arbitrales (PR E P2) -----------------------------------

export type DecisionTypeKind =
  | "PROCEDURAL_ORDER"
  | "PARTIAL_AWARD"
  | "FINAL_AWARD";

export type DecisionStatusKind = "DRAFT" | "SIGNED";

export interface DecisionOut {
  id: number;
  case_file_id: number;
  author_user_id: string;
  decision_type: string;
  title: string;
  content_markdown: string;
  status: string;
  confidens_signature_id: string | null;
  confidens_cert_id: string | null;
  signed_at: string | null;
  signed_pdf_hash: string | null;
  signed_pdf_size: number | null;
  evidence_bundle_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DecisionCreatePayload {
  case_file_id: number;
  decision_type?: DecisionTypeKind;
  title: string;
  content_markdown: string;
}

export interface DecisionUpdatePayload {
  title?: string;
  content_markdown?: string;
}

export interface DecisionSignResponse {
  decision_id: number;
  signature_id: string;
  signed_at: string;
  signed_pdf_hash: string;
  is_simulated: boolean;
  message: string;
}

// =============================================================================
// Skills runner (PR 11 — branche le catalog backend PR 6-9)
// =============================================================================
// 6 skills exposees au stade actuel :
//   chronology-extract, section-drafting, canvas-generate, intake-dossier,
//   validate-art20, calculate-fees.
// Le payload est libre (JSON) et propre a chaque skill ; on documente
// les exemples dans le composant SkillRunner. Le contrat de retour est
// uniforme, garanti par backend/services/skills_registry.py.

export interface SkillCatalogEntry {
  name: string;
}

export interface SkillCatalog {
  skills: SkillCatalogEntry[];
}

export interface SkillRunResponse {
  artifact_id: number;
  artifact_type: string;
  status: string;
  version: number;
  content: Record<string, unknown>;
  confidence: number | null;
  warnings: string[];
  blocking_issues: string[];
  audit_entry_id: number | null;
}

// --- Versions de texte des consentements ------------------------------

export const AUDIENCE_VISIO_CONSENT_VERSION = "audience-visio-v1.0";
export const PLATFORM_TOS_CONSENT_VERSION = "platform-tos-v1.0";
export const PRIVACY_POLICY_CONSENT_VERSION = "privacy-policy-v1.0";
export const RBAC_SCOPE_CONSENT_VERSION = "rbac-scope-acceptation-v1.0";

// --- Consentements ----------------------------------------------------

export async function postAudienceVisioConsent(
  caseFileId: number,
  consentTextVersion: string = AUDIENCE_VISIO_CONSENT_VERSION
): Promise<ConsentResponse> {
  const { data } = await apiClient.post<ConsentResponse>("/api/v1/consents/", {
    consent_type: "AUDIENCE_VISIO",
    consent_text_version: consentTextVersion,
    case_file_id: caseFileId,
  });
  return data;
}

export async function postPlatformTosConsent(): Promise<ConsentResponse> {
  const { data } = await apiClient.post<ConsentResponse>("/api/v1/consents/", {
    consent_type: "PLATFORM_TOS",
    consent_text_version: PLATFORM_TOS_CONSENT_VERSION,
  });
  return data;
}

export async function postRbacScopeAcceptanceConsent(
  caseFileId: number,
  consentTextVersion: string = RBAC_SCOPE_CONSENT_VERSION
): Promise<ConsentResponse> {
  const { data } = await apiClient.post<ConsentResponse>("/api/v1/consents/", {
    consent_type: "RBAC_SCOPE_ACCEPTATION",
    consent_text_version: consentTextVersion,
    case_file_id: caseFileId,
  });
  return data;
}

// Etat serveur d'un consentement plateforme (CGU / Privacy).
// Utilise par PlatformTosOnboardingDialog avant d'afficher la modale
// pour eviter de la presenter a un user qui a deja consenti (depuis
// un autre navigateur, apres localStorage clear, etc.). Le backend
// renvoie accepted=true des qu'une ligne valide existe pour la version
// demandee.
export async function getPlatformTosStatus(): Promise<ConsentStatusResponse> {
  const { data } = await apiClient.get<ConsentStatusResponse>(
    "/api/v1/consents/status",
    {
      params: {
        consent_type: "PLATFORM_TOS",
        consent_text_version: PLATFORM_TOS_CONSENT_VERSION,
      },
    }
  );
  return data;
}

// --- Meetings ---------------------------------------------------------

export async function requestMeetingToken(
  caseFileId: number
): Promise<MeetingTokenResponse> {
  const { data } = await apiClient.post<MeetingTokenResponse>(
    `/api/v1/meetings/${caseFileId}/token`,
    { audience_consent_acknowledged: true }
  );
  return data;
}

// --- Cases ------------------------------------------------------------

export async function listMyCases(): Promise<CaseSummary[]> {
  const { data } = await apiClient.get<CaseSummary[]>("/api/v1/cases");
  return ensureArray<CaseSummary>(data, "listMyCases");
}

export async function getCaseDetail(caseFileId: number): Promise<CaseDetail> {
  const { data } = await apiClient.get<CaseDetail>(
    `/api/v1/cases/${caseFileId}`
  );
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    // eslint-disable-next-line no-console
    console.warn("[api] getCaseDetail: reponse malformee", { data });
    throw new Error("Reponse API invalide (CaseDetail attendu).");
  }
  return {
    ...data,
    documents: ensureArray<DocumentInCaseSummary>(
      (data as CaseDetail).documents,
      "getCaseDetail.documents"
    ),
    members: ensureArray<CaseMemberSummary>(
      (data as CaseDetail).members,
      "getCaseDetail.members"
    ),
  };
}

export async function getAnchorStatus(
  caseFileId: number
): Promise<AnchorStatusResponse> {
  const { data } = await apiClient.get<AnchorStatusResponse>(
    `/api/v1/cases/${caseFileId}/anchor/status`
  );
  return data;
}

export async function triggerCaseAnchor(
  caseFileId: number
): Promise<AnchorTriggerResponse> {
  const { data } = await apiClient.post<AnchorTriggerResponse>(
    `/api/v1/cases/${caseFileId}/anchor`
  );
  return data;
}

export async function getLegalTransitions(
  caseFileId: number
): Promise<LegalTransitionsResponse> {
  const { data } = await apiClient.get<LegalTransitionsResponse>(
    `/api/v1/cases/${caseFileId}/legal-transitions`
  );
  return data;
}

export async function postValidateStep(
  caseFileId: number,
  newState: DossierStatut
): Promise<ValidateStepResponse> {
  const { data } = await apiClient.post<ValidateStepResponse>(
    `/api/v1/cases/${caseFileId}/validate-step`,
    { new_state: newState }
  );
  return data;
}

// --- Documents --------------------------------------------------------

export async function uploadCaseDocument(
  caseFileId: number,
  file: File
): Promise<DocumentUploadResponse> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("dossier_id", String(caseFileId));
  const { data } = await apiClient.post<DocumentUploadResponse>(
    "/api/v1/documents/",
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export interface DocumentVerificationReport {
  status: string;
  evidence_bundle_id: string;
  recalculated_hash?: string | null;
  error_details?: string | null;
  is_high_strength_proof?: boolean;
  checks?: Record<string, boolean>;
}

export async function verifyDocument(
  evidenceBundleId: string
): Promise<DocumentVerificationReport> {
  const { data } = await apiClient.get<DocumentVerificationReport>(
    `/api/v1/documents/${evidenceBundleId}/verify`
  );
  return data;
}

export async function downloadDocument(
  evidenceBundleId: string,
  filename: string
): Promise<void> {
  const response = await apiClient.get(
    `/api/v1/documents/${evidenceBundleId}/download`,
    { responseType: "blob" }
  );
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Decisions arbitrales (PR E P2) -----------------------------------

export async function listCaseDecisions(
  caseFileId: number
): Promise<DecisionOut[]> {
  const { data } = await apiClient.get<DecisionOut[]>(
    `/api/v1/decisions/case/${caseFileId}`
  );
  return ensureArray<DecisionOut>(data, "listCaseDecisions");
}

export async function createDecision(
  payload: DecisionCreatePayload
): Promise<DecisionOut> {
  const { data } = await apiClient.post<DecisionOut>(
    "/api/v1/decisions/",
    payload
  );
  return data;
}

export async function updateDecision(
  id: number,
  payload: DecisionUpdatePayload
): Promise<DecisionOut> {
  const { data } = await apiClient.patch<DecisionOut>(
    `/api/v1/decisions/${id}`,
    payload
  );
  return data;
}

export async function signDecision(id: number): Promise<DecisionSignResponse> {
  const { data } = await apiClient.post<DecisionSignResponse>(
    `/api/v1/decisions/${id}/sign`
  );
  return data;
}

export async function downloadDecisionPdf(
  id: number,
  filename: string
): Promise<void> {
  const response = await apiClient.get(`/api/v1/decisions/${id}/pdf`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Bundle probatoire (PR 10 backend) --------------------------------

/**
 * Telecharge le bundle probatoire ZIP d'une decision (PR 10).
 *
 * Le bundle est livrable des l'etat DRAFT — utile pour audit interne
 * avant signature qualifiee. Apres signature, re-telecharger livre une
 * version plus complete incluant `signed_decision.bin`.
 *
 * Le `filename` par defaut suit le pattern `evidence-bundle-{bundle_id}.zip`
 * communique par le serveur dans Content-Disposition ; on ne le parse
 * pas pour rester simple — on laisse le caller decider du nom.
 */
export async function downloadEvidenceBundle(
  decisionId: number,
  filename: string
): Promise<void> {
  const response = await apiClient.get(
    `/api/v1/decisions/${decisionId}/evidence-bundle`,
    { responseType: "blob" }
  );
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Skills runner (PR 6-9 backend) -----------------------------------

/**
 * Recupere le catalogue des skills disponibles pour ce dossier.
 *
 * Le catalog est borne par les @register_skill cote core ; il evolue
 * a chaque PR (PR 6: chronology-extract + section-drafting ; PR 7:
 * +canvas-generate +intake-dossier ; PR 9: +validate-art20 +calculate-fees).
 * Le frontend N'A PAS a hardcoder la liste — c'est le point du runner.
 */
export async function listSkills(caseId: number): Promise<SkillCatalogEntry[]> {
  const { data } = await apiClient.get<SkillCatalog>(
    `/api/v1/cases/${caseId}/skills`
  );
  return ensureArray<SkillCatalogEntry>(data?.skills ?? [], "listSkills");
}

/**
 * Execute une skill et materialise son resultat en Artifact + audit.
 *
 * Le `payload` est libre (specifique a chaque skill — cf. docstrings
 * cote core dans backend/services/skills/). Timeout etendu a 60s :
 * les skills LLM (sections narratives) peuvent durer 15-30s.
 */
export async function runSkill(
  caseId: number,
  skillName: string,
  payload: Record<string, unknown>
): Promise<SkillRunResponse> {
  const { data } = await apiClient.post<SkillRunResponse>(
    `/api/v1/cases/${caseId}/skills/${encodeURIComponent(skillName)}/run`,
    payload,
    { timeout: 60000 }
  );
  return data;
}

// --- Notifications ----------------------------------------------------

export async function listUnreadNotifications(): Promise<NotificationOut[]> {
  const { data } = await apiClient.get<NotificationOut[]>(
    "/api/v1/notifications/unread"
  );
  return ensureArray<NotificationOut>(data, "listUnreadNotifications");
}

export async function markNotificationRead(
  id: number
): Promise<NotificationOut> {
  const { data } = await apiClient.post<NotificationOut>(
    `/api/v1/notifications/${id}/mark-read`
  );
  return data;
}

// --- Profil -----------------------------------------------------------

export async function getMyProfile(): Promise<ProfileOut> {
  const { data } = await apiClient.get<ProfileOut>("/api/v1/profiles/me");
  return data;
}

export async function updateMyProfile(
  payload: ProfileUpdate
): Promise<ProfileOut> {
  const { data } = await apiClient.patch<ProfileOut>(
    "/api/v1/profiles/me",
    payload
  );
  return data;
}

export async function deleteMyAiData(): Promise<void> {
  await apiClient.delete("/api/v1/users/me/ai-data");
}

// --- Recherche RAG v1 -------------------------------------------------

// Timeout dedie aux endpoints LLM : la generation Mistral (syllogisme +
// claims + critique) prend typiquement 10-30s en mode reel
// (IA_SIMULATION_MODE=false). Le defaut 15s de l'apiClient declenchait un
// ECONNABORTED puis "Reseau indisponible" cote UI alors que le backend
// repondait correctement quelques secondes apres. Cf. post-mortem 1er juin
// 2026 — la signature du bug etait code: "ECONNABORTED" + message: "timeout
// of 15000ms exceeded" + status: 0, masquee par classifyApiError en kind:
// "network" parce que error.response est undefined sur un abort client.
//
// 90s couvre largement les pires cas Mistral observes, reste sous le seuil
// proxy Railway (~120s) et sous le seuil Cloudflare (100s non-Enterprise).
const AI_GENERATION_TIMEOUT_MS = 90_000;

export async function askJurisIA(
  question: string,
  caseFileId?: number
): Promise<RechercheResponse> {
  const { data } = await apiClient.post<RechercheResponse>(
    "/api/v1/recherche/ask",
    { question, case_file_id: caseFileId ?? null },
    { timeout: AI_GENERATION_TIMEOUT_MS }
  );
  return data;
}

// --- Recherche RAG v2 (Lots 2-3-6 — derrière IA_V2_ENABLED côté backend) -

export interface AskJurisIAv2Options {
  caseFileId?: number;
  mode?: ResponseMode;
}

export async function askJurisIAv2(
  question: string,
  options: AskJurisIAv2Options = {}
): Promise<RechercheResponseV2> {
  const { data } = await apiClient.post<RechercheResponseV2>(
    "/api/v1/recherche/ask/v2",
    {
      question,
      case_file_id: options.caseFileId ?? null,
      mode: options.mode ?? "quick",
    },
    { timeout: AI_GENERATION_TIMEOUT_MS }
  );
  return data;
}

// --- Assistant IA de redaction de sentence ----------------------------
//
// Endpoints `/api/v1/sentences/*` (backend/api/sentence_routes.py).
// Conformite : AI Act Art. 14 (HITL obligatoire), OHADA AUA 2017 Art. 20
// (mentions obligatoires), CCJA Art. 24 (frais).

export type SentenceSectionType =
  | "motifs"
  | "dispositif"
  | "procedure"
  | "pretentions"
  | "moyens";

export interface SentenceSectionGenerateRequest {
  case_file_id: number;
  section_type: SentenceSectionType;
  context_document_ids?: number[];
  instructions?: string;
}

export interface SentenceSectionGenerateResponse {
  generated_text: string;
  confidence_score: number;
  section_type: SentenceSectionType;
  requires_validation: boolean;
  ai_act_compliance: Record<string, boolean>;
  model_used: string;
  simulated: boolean;
}

export interface Art20ValidationRequest {
  content_sections: Record<string, string>;
}

export interface Art20ValidationResponse {
  is_compliant: boolean;
  missing_sections: string[];
  warnings: string[];
  compliance_score: number;
  total_required_sections: number;
  filled_sections: number;
}

export interface CcjaFeesRequest {
  montant_litige: number;
  devise: "XOF" | "EUR" | "USD";
  nombre_arbitres: 1 | 2 | 3;
}

export interface CcjaFeesResponse {
  montant_litige: number;
  devise: string;
  nombre_arbitres: number;
  honoraires_arbitres: number;
  frais_administratifs: number;
  total: number;
  bareme_reference: string;
  taux_applique: number;
}

export interface OhadaClause {
  id: string;
  title: string;
  content: string;
  category: string;
  article_reference: string;
}

export async function generateSentenceSection(
  payload: SentenceSectionGenerateRequest
): Promise<SentenceSectionGenerateResponse> {
  // Generation LLM longue duree — meme rationale que askJurisIA :
  // le defaut 15s timeout client cause un ECONNABORTED avant que Mistral
  // ne reponde. AI_GENERATION_TIMEOUT_MS s'applique aussi ici.
  const { data } = await apiClient.post<SentenceSectionGenerateResponse>(
    "/api/v1/sentences/generate-section",
    payload,
    { timeout: AI_GENERATION_TIMEOUT_MS }
  );
  return data;
}

export async function validateArt20(
  sections: Record<string, string>
): Promise<Art20ValidationResponse> {
  const { data } = await apiClient.post<Art20ValidationResponse>(
    "/api/v1/sentences/validate-art20",
    { content_sections: sections }
  );
  return data;
}

export async function calculateCcjaFees(
  payload: CcjaFeesRequest
): Promise<CcjaFeesResponse> {
  const { data } = await apiClient.post<CcjaFeesResponse>(
    "/api/v1/sentences/calculate-fees",
    payload
  );
  return data;
}

export async function listOhadaClauses(
  category?: string
): Promise<OhadaClause[]> {
  const { data } = await apiClient.get<OhadaClause[]>(
    "/api/v1/sentences/clauses",
    category ? { params: { category } } : undefined
  );
  return ensureArray<OhadaClause>(data, "listOhadaClauses");
}

/**
 * Recupere un document du corpus public par son UUID Qdrant (PR C).
 * Utilise par la page `/corpus/:id` pour rendre la fiche source quand
 * l'utilisateur clique sur une assertion juridique dans ClaimsList.
 *
 * - 404 : document introuvable OU non public (le backend ne fait pas
 *   la difference, anti-leak).
 * - 401 : non authentifie.
 * - 503 : Qdrant indisponible.
 */
export async function fetchCorpusDocument(
  sourceId: string
): Promise<CorpusDocument> {
  const { data } = await apiClient.get<CorpusDocument>(
    `/api/v1/corpus/${encodeURIComponent(sourceId)}`
  );
  return data;
}

// --- Cockpit / Maison numerique de l'arbitre --------------------------
//
// Branchements sur les endpoints backend `/api/v1/arbiter/dashboard/*`
// implementes en Phase 2 MVP Cockpit (backend/api/dashboard.py +
// backend/schemas/dashboard.py). Les types miroitent les schemas
// Pydantic cote core.

export type CockpitActionType =
  | "review_document"
  | "schedule_hearing"
  | "send_notification"
  | "complete_milestone"
  | "resolve_bottleneck"
  | "validate_sentence";

export type CockpitPriority = "low" | "medium" | "high" | "critical";

export interface CockpitNextBestAction {
  id: string;
  case_id: number;
  case_reference: string;
  action_type: CockpitActionType;
  title: string;
  description: string;
  priority: CockpitPriority;
  due_date: string | null;
  ai_confidence: number;
  ai_sources: string[];
  ai_reasoning: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface CockpitCaseSummary {
  id: number;
  reference: string;
  objet_litige: string;
  statut: string;
  created_at: string;
  deadline_instruction: string | null;
  days_remaining: number | null;
  risk_score: number | null;
  progress_percentage: number | null;
  anchored_status: string;
}

export interface CockpitNotificationSummary {
  id: number;
  title: string;
  message: string;
  reference_dossier: string | null;
  is_read: boolean;
  created_at: string;
}

export interface CockpitStats {
  total_cases: number;
  active_cases: number;
  completed_cases: number;
  urgent_cases: number;
  avg_resolution_days: number | null;
}

export interface CockpitDashboardView {
  arbiter_id: string;
  arbiter_name: string;
  stats: CockpitStats;
  cases: CockpitCaseSummary[];
  notifications: CockpitNotificationSummary[];
  next_best_actions: CockpitNextBestAction[];
  global_insight: string | null;
  generated_at: string;
  cache_valid_until: string | null;
}

export interface CockpitInstantInsight {
  case_id: number;
  case_reference: string;
  summary: string;
  key_points: string[];
  status_assessment: string;
  risk_factors: string[];
  days_elapsed: number;
  days_remaining: number;
  progress_percentage: number;
  on_track: boolean;
  next_steps: string[];
  ai_confidence: number;
  ai_sources: string[];
  generated_at: string;
}

export async function getCockpitDashboard(): Promise<CockpitDashboardView> {
  // Le dashboard agrege cases + notifications + AI actions + insights —
  // sur un cold-start Railway ou une instance qui se reveille, le 15 s
  // global d'apiClient peut etre court. On etend a 45 s uniquement
  // pour cet appel.
  const { data } = await apiClient.get<CockpitDashboardView>(
    "/api/v1/arbiter/dashboard",
    { timeout: 45_000 }
  );
  return data;
}

export async function getCockpitInsight(
  caseId: number
): Promise<CockpitInstantInsight> {
  const { data } = await apiClient.get<CockpitInstantInsight>(
    `/api/v1/arbiter/dashboard/insights/${caseId}`
  );
  return data;
}

export async function getCockpitActions(
  limit = 5
): Promise<CockpitNextBestAction[]> {
  const { data } = await apiClient.get<CockpitNextBestAction[]>(
    "/api/v1/arbiter/dashboard/actions",
    { params: { limit } }
  );
  return ensureArray<CockpitNextBestAction>(data, "getCockpitActions");
}

// --- Erreurs ----------------------------------------------------------

export function getApiErrorDetail(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = (error as AxiosError<{ detail?: string }>).response?.data;
    if (data && typeof data.detail === "string") return data.detail;
  }
  return null;
}

export function isArt52Error(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 403)
    return false;
  const detail = (error.response.data as { detail?: string } | undefined)
    ?.detail;
  return typeof detail === "string" && detail.includes("AI_ACT_ART52");
}
